import * as React from "react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Typography, App, theme, Progress } from "antd";
import { CirclePlay } from "lucide-react";
import Icon from "../../components/Icon";
import { alpha } from "../../utils/color";
import { useThemeMode } from "../../theme/ThemeProvider";
import ModernModuleGrid from "./components/ModernModuleGrid";
import { useAuth } from "../../auth/auth.context";
import { useUserProgress } from "../../hooks/useUserProgress";
import { fetchCoursesWithModules } from "../../services/moduleService";
import { getSafeImageUrl } from "../../utils/imageHelper";
import AppLoadingScreen from "../../components/AppLoadingScreen";

import MainLayout from "../../components/MainLayout";

const { Title, Text } = Typography;
const { useToken } = theme;
const COURSE_FALLBACK_IMAGE = "/no-image.jpg";

const clampProgress = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.min(100, Math.max(0, numericValue));
};

export default function Homepage() {
  const [showWelcome, setShowWelcome] = React.useState(false);
  const [allModules, setAllModules] = React.useState([]);
  const [rawModules, setRawModules] = React.useState([]);
  const [isModulesLoading, setIsModulesLoading] = React.useState(true);

  const { currentUser, userProfile, loading } = useAuth(); // Lấy loading từ AuthProvider
  const navigate = useNavigate();
  const {
    loading: progressLoading,
    userProgress,
    isModuleCompleted,
    isModuleInProgress,
    getProgressModuleVersion,
  } = useUserProgress();
  const { mode } = useThemeMode();
  const { token } = useToken();
  const { message } = App.useApp();

  // Fetch all modules khi mount
  React.useEffect(() => {
    let mounted = true;

    async function fetchData() {
      setIsModulesLoading(true);
      try {
        const data = await fetchCoursesWithModules(currentUser?.uid);
        if (!mounted) return;
        setRawModules(data);

        // Flatten modules từ container và standalone
        const flatModules = [];
        data.forEach((item) => {
          if (item.isContainer && item.modules) {
            flatModules.push(...item.modules);
          } else if (!item.isContainer) {
            flatModules.push(item);
          }
        });
        setAllModules(flatModules);
      } catch (error) {
        console.error("Error fetching modules:", error);
      } finally {
        if (mounted) {
          setIsModulesLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      mounted = false;
    };
  }, [currentUser?.uid]);
  // Determine which button to show and navigation logic
  const comingSoonModuleIds = new Map(
    rawModules
      .filter((item) => item.isContainer && item.isComingSoon && item.modules)
      .flatMap((item) => item.modules.map((m) => [m.id, true]))
  );

  const getButtonAction = () => {
    if (!currentUser || progressLoading || allModules.length === 0) {
      return null;
    }

    // Tìm module đầu tiên chưa hoàn thành hoặc mới cập nhât (theo thứ tự)
    const firstUncompleted = allModules.find(
      (module) =>
        !comingSoonModuleIds.get(module.id) &&
        (!isModuleCompleted(module.id) ||
          getProgressModuleVersion(module.id) < module.version)
    );
    const hasAnyProgress = allModules.some(
      (m) => isModuleCompleted(m.id) || isModuleInProgress(m.id)
    );
    if (firstUncompleted) {
      // Nếu user chưa học gì (userProgress rỗng) thì là Start Learning, còn lại luôn là Continue Learning
      return {
        type: hasAnyProgress ? "continue" : "start",
        label: hasAnyProgress ? "Resume" : "Start Learning",
        moduleId: firstUncompleted.id,
        icon: <Icon as={CirclePlay} />,
      };
    }

    // Nếu đã hoàn thành hết, ẩn nút
    return null;
  };

  const handleButtonClick = () => {
    const action = getButtonAction();
    if (action) {
      navigate(`/module/${action.moduleId}`);
    }
  };

  const handleModuleClick = (module) => {
    if (module.isLocked) return;
    navigate(`/module/${module.id}`);
  };

  // Chỉ refresh data khi cần thiết (khi user thay đổi)
  useEffect(() => {
    if (currentUser) {
      // Chỉ refresh khi user thay đổi, không phải mỗi lần mount
      // Context đã tự động handle việc load data, không cần dispatch event nữa
    }
  }, [currentUser]); // Chỉ phụ thuộc vào currentUser

  // Show welcome message for new users
  useEffect(() => {
    if (currentUser) {
      // Listen for user initialization events
      const handleUserInitialized = (event) => {
        const { isNewUser } = event.detail;
        if (isNewUser) {
          setShowWelcome(true);
        }
      };

      window.addEventListener("userInitialized", handleUserInitialized);

      return () => {
        window.removeEventListener("userInitialized", handleUserInitialized);
      };
    }
  }, [currentUser]);

  // Show welcome message via antd message API
  useEffect(() => {
    if (showWelcome) {
      message.success(
        "Welcome! Your profile has been created with default role: Installer. You can change your role anytime by clicking the Role button in the header."
      );
      setShowWelcome(false);
    }
  }, [showWelcome, message]);

  const buttonAction = getButtonAction();

  const getResumeContainer = () => {
    if (!buttonAction) return null;
    return rawModules.find(item => item.isContainer && item.modules?.some(m => m.id === buttonAction.moduleId));
  };

  const getResumeModule = () => {
    if (!buttonAction) return null;
    return allModules.find(m => m.id === buttonAction.moduleId);
  };

  const getResumeProgressPercent = () => {
    if (!buttonAction) return 0;

    const container = getResumeContainer();
    if (container && container.modules && container.modules.length > 0) {
      const completedCount = container.modules.filter(m => isModuleCompleted(m.id)).length;
      return clampProgress((completedCount / container.modules.length) * 100);
    }

    const module = getResumeModule();
    const currentStepId = userProgress[buttonAction.moduleId]?.currentStepId;
    if (module && currentStepId) {
      const stepNumber = parseInt(currentStepId.replace(/\D/g, ""), 10);
      return clampProgress((stepNumber / (module.totalSteps || 1)) * 100);
    }

    return 0;
  };

  // Kiểm tra xem user đã hoàn thành tất cả modules chưa (bỏ qua module thuộc course Coming Soon)
  const isAllCompleted =
    allModules.length > 0 &&
    allModules
      .filter((module) => !comingSoonModuleIds.get(module.id))
      .every((module) => isModuleCompleted(module.id));

  // Nếu AuthProvider còn loading thì không render gì (đã có splash ở AuthProvider)

  return (
    <MainLayout showHomeButton={false}>
      {loading || progressLoading || isModulesLoading ? (
        <AppLoadingScreen />
      ) : (
        <div
          className="homepage-container"
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            padding: "0px 24px",
            width: "100%",
          }}
        >
          <style>{`
            h1.ant-typography.welcome-title {
              font-family: var(--font-untitled-sans) !important;
              font-size: 2.5rem !important;
              letter-spacing: normal !important;
              line-height: 1.1 !important;
              font-weight: 400 !important;
              text-transform: none !important;
            }
            h2.ant-typography.section-title {
              font-family: var(--font-untitled-sans) !important;
              font-size: 2rem !important;
              letter-spacing: normal !important;
              font-weight: 400 !important;
              text-transform: none !important;
            }
            .resume-card {
              background: ${mode === "dark" ? token.colorBgContainer : "#fff"};
              border: 1px solid ${token.colorBorderSecondary};
              border-radius: ${token.borderRadius}px;
              overflow: hidden;
              transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
              display: flex;
              flex-direction: row;
              height: 250px;
              cursor: pointer;
            }
            .resume-card-image-container {
              flex: 0 0 35%;
              position: relative;
              overflow: hidden;
              height: 100%;
            }
            .resume-content {
              flex: 1;
              padding: 16px 24px;
              display: flex;
              flex-direction: column;
              justify-content: center;
              font-family: var(--font-untitled-sans);
            }
            .resume-content h2 {
              font-size: 1.5rem !important;
              margin-bottom: 4px !important;
            }
            .resume-description {
              font-size: 0.9rem !important;
              margin-bottom: 12px !important;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
            }
            @media (max-width: 768px) {
              .homepage-container {
                padding: 16px !important;
              }
              .welcome-title {
                font-size: 1.4rem !important;
                line-height: 1.2 !important;
              }
              .section-title {
                font-size: 1.4rem !important;
                letter-spacing: 2px !important;
              }
              .resume-card {
                flex-direction: column;
                height: auto !important;
                min-height: auto;
                border-radius: ${token.borderRadius}px;
              }
              .resume-card-image-container {
                flex: 0 0 160px; /* Reduced from 200px */
              }
              .resume-content {
                padding: 16px 20px !important; /* Reduced from 24px */
              }
              .resume-content h2 {
                font-size: 1.3rem !important; /* Slightly smaller */
                margin-bottom: 4px !important;
              }
              .hero-section-container {
                margin-bottom: 32px !important; /* Reduced from 40px */
              }
            }
            .resume-card:hover {
              box-shadow: 0 20px 40px ${alpha(token.colorText, 0.08)};
            }
            .resume-card-image {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }
            .resume-btn-overlay {
              position: absolute;
              bottom: 24px;
              right: 24px;
              z-index: 2;
              opacity: 1;
            }
            .progress-tag {
              display: inline-block;
              padding: 4px 12px;
              background: ${alpha(token.colorText, 0.1)};
              color: ${token.colorText};
              border-radius: ${token.borderRadius / 2}px;
              font-size: 11px;
              font-weight: 700;
              letter-spacing: 1px;
            }
          `}</style>
          <div style={{ marginBottom: 40 }}>
            <Title level={1} className="welcome-title" style={{ marginBottom: 8, color: token.colorText }}>
              Welcome back, {userProfile?.displayName?.split(" ")[0] || currentUser?.displayName?.split(" ")[0] || "Learner"}.
            </Title>
            <Text type="secondary" style={{ fontSize: "1.1rem", fontFamily: "var(--font-untitled-sans)" }}>
              Ready to continue expanding your expertise?
            </Text>
          </div>

          {buttonAction && (
            <div className="hero-section-container" style={{ marginBottom: 64 }}>
              <div className="resume-card" onClick={handleButtonClick}>
                <div className="resume-card-image-container">
                  <img 
                    src={getSafeImageUrl(
                      getResumeContainer()?.thumbnail ||
                      getResumeModule()?.thumbnail
                    ) || COURSE_FALLBACK_IMAGE}
                    alt="Current Course" 
                    className="resume-card-image"
                    onError={(event) => {
                      if (!event.currentTarget.src.endsWith(COURSE_FALLBACK_IMAGE)) {
                        event.currentTarget.src = COURSE_FALLBACK_IMAGE;
                      }
                    }}
                  />
                  <div className="resume-btn-overlay">
                    <Button 
                      type="primary" 
                      size="large" 
                      icon={<Icon as={CirclePlay} />}
                      style={{ 
                        borderRadius: token.borderRadius / 2, 
                        height: 48, 
                        padding: "0 24px",
                        fontWeight: 400,
                        background: token.colorPrimary,
                        color: token.colorTextLightSolid,
                        border: "none",
                        boxShadow: `0 8px 16px ${alpha(token.colorPrimary, 0.2)}`,
                        fontFamily: "var(--font-untitled-sans)"
                      }}
                    >
                      {buttonAction.label}
                    </Button>
                  </div>
                </div>
                <div className="resume-content">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <span className="progress-tag">
                      {buttonAction.type === "continue" ? "IN PROGRESS" : "NOT STARTED"}
                    </span>
                    <Text type="secondary" style={{ fontSize: 13, fontWeight: 400, fontFamily: "var(--font-untitled-sans)" }}>
                      {(() => {
                        const currentModuleId = buttonAction.moduleId;
                        const container = getResumeContainer();
                        if (container) {
                          const indexInContainer = container.modules.findIndex(m => m.id === currentModuleId) + 1;
                          return `Module ${indexInContainer} of ${container.modules.length}`;
                        }
                        return `Module ${allModules.findIndex(m => m.id === currentModuleId) + 1} of ${allModules.length}`;
                      })()}
                    </Text>
                  </div>
                  
                  <Title level={2} style={{ margin: "0 0 8px 0", textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 400, fontFamily: "var(--font-sf-pro)" }}>
                    {(() => {
                        const container = getResumeContainer();
                        return container ? container.name : getResumeModule()?.name;
                    })()}
                  </Title>
                  
                  <Text type="secondary" className="resume-description" style={{ fontFamily: "var(--font-untitled-sans)" }}>
                    {allModules.find(m => m.id === buttonAction.moduleId)?.name}: {allModules.find(m => m.id === buttonAction.moduleId)?.description}
                  </Text>
                  
                  <div style={{ marginTop: "auto" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <Text strong style={{ fontSize: 14, fontFamily: "var(--font-untitled-sans)" }}>
                        {Math.round(getResumeProgressPercent())}% Complete
                      </Text>
                    </div>
                    <Progress 
                      percent={Math.round(getResumeProgressPercent())}
                      showInfo={false} 
                      strokeColor={token.colorText} 
                      trailColor={alpha(token.colorText, 0.06)}
                      strokeWidth={8}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            {rawModules.length > 0 && (
              <div style={{ marginBottom: 32 }}>
                <Title level={2} className="section-title" style={{ margin: 0 }}>
                  Browse All Courses
                </Title>
              </div>
            )}
            <ModernModuleGrid
              moduleFromHomePage={rawModules}
              userProgressData={{
                loading: progressLoading,
                userProgress,
                isModuleCompleted,
                isModuleInProgress,
                getCurrentStepId: (moduleId) => {
                  const progress = userProgress[moduleId] || null;
                  return progress?.currentStepId || progress?.stepId || null;
                }
              }}
              onModuleClick={handleModuleClick}
              isUpdate={true}
            />
          </div>
        </div>
      )}
    </MainLayout>
  );
}
